<template>
    <div class="porting-tab row nav-padding">
        <div class="col-3">
            <company-autosuggest
                v-model="company_id"
                @input="onChangeTargetCompany"
                placeholder="Search a company by ID, SID or name"
            />
        </div>

        <div class="col-4">
            <line-autosuggest
                v-model="line_id"
                :company_id="company_id"
                :porting="true"
                @input="onChangeTargetLine"
                :disabled="!company_id"
                :phone_number_search="phone_number_search"
            />
        </div>

        <div class="col-12 mt-3">
            <div class="row">
                <div class="col-3">
                    <el-input
                        v-model="phone_number_search"
                        :disabled="!line_id"
                        clearable
                        placeholder="Type to search"
                        class="mb-0"
                        size="small"
                    />
                </div>
                <div class="col-3">
                    <el-button
                        type="primary"
                        @click="markAsPortedOut"
                        size="small"
                        :disabled="!hasSelectedNumbers || request_in_progress"
                    >
                        Mark as Ported Out
                    </el-button>
                </div>
            </div>
        </div>

        <div class="col-12">
            <el-table
                :data="searchedPhoneNumbers"
                @selection-change="handleNumberSelectionChange"
                row-key="line_id"
            >
                <el-table-column
                    type="selection"
                    :selectable="isRowSelectable"
                    width="55"
                />

                <el-table-column
                    prop="id"
                    label="ID"
                    width="120"
                />

                <el-table-column
                    prop="phone_number"
                    label="Phone Number"
                    width="250"
                >
                    <template v-slot="scope">
                        {{ cleanupNumber(scope.row) }}
                    </template>
                </el-table-column>

                <el-table-column
                    prop="porting_status"
                    label="Porting Status"
                >
                    <template v-slot="scope">
                        <b
                            class="label"
                            :class="scope.row.porting_status | portingStatusColor"
                        >
                            <span>{{ scope.row.porting_status | portingStatusName }}</span>
                        </b>
                        <b
                            v-if="scope.row.sid.includes('fake-')"
                            class="label dark"
                        >
                            <span>Fake</span>
                        </b>
                    </template>
                </el-table-column>

                <el-table-column
                    prop="ported_out_at"
                    label="Ported Out At"
                >
                    <template v-slot="scope">
                        <span v-if="scope.row.ported_out_at">{{ scope.row.ported_out_at | fixDateTime }}</span>
                        <span v-else>-</span>
                    </template>
                </el-table-column>

                <el-table-column
                    prop="ported_out_by"
                    label="Ported Out By"
                >
                    <template v-slot="scope">
                        <span v-if="scope.row.porter_out">#{{ scope.row.porter_out.id }} - {{ scope.row.porter_out.name }}</span>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <el-dialog
            title="Confirm"
            width="30%"
        >
            <div>
                <p>Are you sure you want to Port Out this number?</p>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios'
import CompanyAutosuggest from '../company-autosuggest'
import LineAutosuggest from '../line-autosuggest'
import * as PortingStatus from '../../constants/porting-statuses'
import { format_mixin } from '../../mixins'

export default {
    components: {
        CompanyAutosuggest,
        LineAutosuggest,
    },

    mixins: [format_mixin],

    data() {
        return {
            incoming_numbers: [],
            selected_number_ids: [],
            lines: [],
            line_id: null,
            company_id: null,
            selected_line: null,
            ported: false,
            request_in_progress: false,
            phone_number_search: '',
        }
    },

    computed: {
        hasSelectedNumbers() {
            return this.selected_number_ids.length > 0
        },

        searchedPhoneNumbers() {
            if (!this.phone_number_search) {
                return this.incoming_numbers
            }

            return this.incoming_numbers.filter((incoming_number) => {
                let searchable_number = incoming_number.phone_number
                if (incoming_number.phone_number.includes('_')) {
                    searchable_number = searchable_number.split('_')[0]
                }

                if (incoming_number.phone_number.includes('+')) {
                    searchable_number = searchable_number.split('+')[1]
                }

                return incoming_number.id.toString().includes(this.phone_number_search) || searchable_number.includes(this.phone_number_search)
            })
        },
    },

    filters: {
        portingStatusColor(status) {
            return (
                {
                    [PortingStatus.STATUS_NONE]: 'gray-600',
                    [PortingStatus.STATUS_PORTED_OUT]: 'blue-600',
                }[status] || 'black-300'
            )
        },
    },

    methods: {
        cleanupNumber(incoming_number) {
            if (!incoming_number.deleted_at) {
                return incoming_number.phone_number
            }

            // Revove everything after the first _
            return this.cleanupDeletedTag(incoming_number.phone_number)
        },

        filterIncomingNumbers(incoming_numbers) {
            // Only show deleted numbers due to port-out
            return incoming_numbers.filter((incoming_number) => {
                if (!incoming_number.deleted_at) {
                    return true
                }

                return incoming_number.porting_status === PortingStatus.STATUS_PORTED_OUT
            })
        },

        onChangeTargetCompany(company_id) {
            this.company_id = company_id
        },

        getIncomingNumbers(line_id) {
            axios.get(`/api/admin/campaigns/${line_id}/incoming-numbers`, {
                params: {
                    ported: true,
                    fakes: true,
                }
            })
                .then((res) => {
                    this.incoming_numbers = res.data
                }).catch((err) => {
                    if (axios.isCancel(err)) {
                        console.log('Request canceled', err.message)
                    } else {
                        console.log(err.response)
                        this.$root.handleErrors(err.response)
                    }
                })
        },

        onChangeTargetLine(line_id) {
            if (!line_id) {
                this.incoming_numbers = []
                return
            }

            this.getIncomingNumbers(line_id)
        },

        handleNumberSelectionChange(rows) {
            // Handle selection change
            this.selected_number_ids = rows.map(({id}) => id)
            console.log('Selected numbers:', this.selected_number_ids)
        },

        isRowSelectable(row) {
            return row.porting_status !== PortingStatus.STATUS_PORTED_OUT
        },

        formatPhoneNumber(phone_number) {
            // Remove the _deleted_timestamp
            return phone_number.split('_')[0]
        },

        markAsPortedOut() {
            // Block the button
            this.request_in_progress = true

            this.$confirm(
                'Are you sure you want to Port Out these numbers? They will no longer be usable.',
                'Warning',
                {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'Cancel',
                    customClass: 'width-500 fixed',
                    type: 'warning',
                }
            )
            .then(() => {
                // Send the request
                axios.post(`/api/admin/campaigns/${this.line_id}/port-out`, {
                    incoming_number_ids: this.selected_number_ids
                }).then((res) => {
                    // Show success message
                    this.$notify({
                        duration: 2000,
                        title: 'Porting',
                        message: res.data.message,
                        type: 'success',
                        showClose: true,
                        position: 'top-right'
                    })

                    this.getIncomingNumbers(this.line_id)
                }).catch((err) => {
                    this.$root.handleErrors(err.response)
                }).finally(() => {
                    // Unblock the button
                    this.request_in_progress = false
                })
            })
            .catch(() => {
                // Unblock the button, if the user cancels
                this.request_in_progress = false
            })
        },
    },

    watch: {
        company_id(val) {
            // Reset lines when company changes and wait for the search
            this.incoming_numbers = []
            this.line_id = null
            this.lines = []
            this.phone_number_search = ''
        },
    },
}
</script>
